import React from "react";
import {
  Button,
  Checkbox,
  Input,
  Space,
  TimePicker,
  Typography,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { Moment } from "moment";
import moment from "moment";
import {
  type DayKey,
  type ScheduleBlock,
  DAY_LABEL_SHORT,
  DAY_ORDER,
  decodeSchedule,
  defaultScheduleBlocks,
  encodeSchedule,
  formatDaysLabel,
  formatTimeHm,
} from "../lib/storeSchedule";

const { Text } = Typography;

type Props = {
  value?: string;
  onChange?: (v: string) => void;
};

const CHECKBOX_OPTIONS: { label: string; value: DayKey }[] = DAY_ORDER.map(
  (value) => ({ value, label: DAY_LABEL_SHORT[value] })
);

function hmToMoment(hm: string | undefined): Moment | null {
  if (!hm?.trim()) return null;
  const m = moment(hm, "HH:mm", true);
  return m.isValid() ? m : null;
}

export const StoreScheduleEditor: React.FC<Props> = ({ value, onChange }) => {
  const decoded = React.useMemo(() => decodeSchedule(value), [value]);
  const [legacyText, setLegacyText] = React.useState(
    decoded.kind === "legacy" ? decoded.text : ""
  );

  React.useEffect(() => {
    const d = decodeSchedule(value);
    if (d.kind === "legacy") setLegacyText(d.text);
  }, [value]);

  const blocks =
    decoded.kind === "structured" ? decoded.blocks : defaultScheduleBlocks();
  const isLegacy = decoded.kind === "legacy";

  const pushBlocks = (next: ScheduleBlock[]) => {
    onChange?.(encodeSchedule(next));
  };

  const updateBlock = (index: number, patch: Partial<ScheduleBlock>) => {
    const next = blocks.map((b, i) =>
      i === index ? { ...b, ...patch } : b
    );
    pushBlocks(next);
  };

  const addBlock = () => {
    pushBlocks([
      ...blocks,
      { days: ["mon"], open: "10:00", close: "20:00" },
    ]);
  };

  const removeBlock = (index: number) => {
    const next = blocks.filter((_, i) => i !== index);
    pushBlocks(next.length ? next : defaultScheduleBlocks());
  };

  const applyPreset = (index: number, preset: DayKey[]) => {
    updateBlock(index, { days: preset });
  };

  if (isLegacy) {
    return (
      <div>
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Este horario está en texto libre. Puede editarlo aquí o pasar al
          selector por días.
        </Text>
        <Input.TextArea
          rows={3}
          value={legacyText}
          onChange={(e) => {
            const t = e.target.value;
            setLegacyText(t);
            onChange?.(t);
          }}
          placeholder="Ej. Lunes a Sábado 10:00 AM – 8:00 PM"
        />
        <Button
          type="link"
          style={{ paddingLeft: 0, marginTop: 4 }}
          onClick={() => {
            onChange?.(encodeSchedule(defaultScheduleBlocks()));
          }}
        >
          Usar selector por días (se reemplaza el texto)
        </Button>
      </div>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={12}>
      {blocks.map((block, index) => (
        <div
          key={index}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 12,
            background: "#fafafa",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <Text strong>Franja {index + 1}</Text>
            {blocks.length > 1 && (
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeBlock(index)}
              >
                Quitar
              </Button>
            )}
          </div>

          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            Días
          </Text>
          <Checkbox.Group
            style={{ width: "100%", marginBottom: 8 }}
            value={block.days}
            onChange={(v) =>
              updateBlock(index, { days: v as DayKey[] })
            }
            options={CHECKBOX_OPTIONS}
          />
          <Space wrap size={[4, 4]} style={{ marginBottom: 10 }}>
            <Button
              size="small"
              onClick={() =>
                applyPreset(index, ["mon", "tue", "wed", "thu", "fri"])
              }
            >
              Lun–Vie
            </Button>
            <Button
              size="small"
              onClick={() =>
                applyPreset(index, [
                  "mon",
                  "tue",
                  "wed",
                  "thu",
                  "fri",
                  "sat",
                ])
              }
            >
              Lun–Sáb
            </Button>
            <Button
              size="small"
              onClick={() => applyPreset(index, ["sun"])}
            >
              Solo domingos
            </Button>
            <Button
              size="small"
              onClick={() => applyPreset(index, ["sun", "holiday"])}
            >
              Dom + festivos
            </Button>
            <Button
              size="small"
              onClick={() => applyPreset(index, [...DAY_ORDER])}
            >
              Todos
            </Button>
          </Space>

          <Space wrap align="center">
            <span style={{ fontSize: 13 }}>Desde</span>
            <TimePicker
              use12Hours
              format="h:mm A"
              value={hmToMoment(block.open)}
              onChange={(m: Moment | null) =>
                updateBlock(index, {
                  open: m ? m.format("HH:mm") : "",
                })
              }
              minuteStep={15}
            />
            <span style={{ fontSize: 13 }}>hasta</span>
            <TimePicker
              use12Hours
              format="h:mm A"
              value={hmToMoment(block.close)}
              onChange={(m: Moment | null) =>
                updateBlock(index, {
                  close: m ? m.format("HH:mm") : "",
                })
              }
              minuteStep={15}
            />
          </Space>
        </div>
      ))}

      <Button type="dashed" onClick={addBlock} block icon={<PlusOutlined />}>
        Añadir otra franja
      </Button>

      <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
        Vista previa:{" "}
        {blocks
          .filter((b) => b.days.length && b.open && b.close)
          .map(
            (b) =>
              `${formatDaysLabel(b.days)}: ${formatTimeHm(b.open)} a ${formatTimeHm(b.close)}`
          )
          .join(" · ")}
      </Text>
    </Space>
  );
};
